import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,

} from "@tanstack/react-table";
import TableHeader from "./TableHeader";
import Pagination from "./Pagination";
import { TableGridSkeleton } from "./TableSkeleton";
import AccessDenied from "../AccessDenied";
import PropTypes from "prop-types";
import { useSelector } from 'react-redux'



function mapV7ColumnsToV8(columns, sortableColumns = []) {
  return columns.map((col) => {
    const isStringAccessor = typeof col.accessor === "string";
    const isFnAccessor = typeof col.accessor === "function";
    const id = col.id || (isStringAccessor ? col.accessor : undefined);

    const def = {
      id,
      header: col.Header ?? col.header ?? id ?? "",
      // enableSorting replaces v7's disableSortBy
      enableSorting: isStringAccessor
        ? sortableColumns.includes(col.accessor)
        : id
          ? sortableColumns.includes(id)
          : false,
    };

    if (isStringAccessor) {
      def.accessorKey = col.accessor; // supports nested "a.b.c"
    } else if (isFnAccessor) {
      def.accessorFn = (row) => col.accessor(row);
    } else if (col.accessorKey) {
      // already v8-style columns? keep them working too
      def.accessorKey = col.accessorKey;
      def.enableSorting = sortableColumns.includes(col.accessorKey);
    }

    if (col.Cell) {
      // v7 Cell({ value, row, column, cell }) -> v8 cell(info)
      def.cell = (info) =>
        col.Cell({
          value: info.getValue?.(),
          row: {
            original: info.row.original,
            index: info.row.index
          },
          column: info.column,
          cell: info.cell,
        });
    }

    if (col.meta) def.meta = col.meta;

    return def;
  });
}

const DataTable = ({
  columns,
  data,
  options = {},
  Search = {},
  Entries = {},
  pagination = {},
  isDate = false,
  date = {
    key: "",
    field: {},
    value: [],
    handleChange: () => { },
    error: "",
    selectMode: "single",
    mode: ""
  }
}) => {
  // ===== General options
  const {
    sortableColumns = [],
    copy = false,
    csv = false,
    pdf = false,
    print = false,
    loading = false,
    error = {
      status: null,
      message: "",
      success: false
    },
    fileName = "table-data",
  } = options;

  // ===== Search settings (passed through to header)
  const { search = false, SearchInputValue = "", handleSearch = () => { } } = Search;

  const user = useSelector((state) => state.user);


  // ===== Entries (page size selector UI only; table pagination is external)
  const {
    Entries: showEntries = false,
    limit = 10,
    onLimitChange = () => { },
    options: sizeOptions = [],
  } = Entries;

  // ===== External pagination controls
  const { isActive = true, meta = {}, onPageChange = () => { } } = pagination;

  // v7 -> v8: build column defs and sorting state
  const modifiedColumns = useMemo(
    () => mapV7ColumnsToV8(columns, sortableColumns),
    [columns, sortableColumns]
  );

  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data,
    columns: modifiedColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // If you later want server-side sorting, set manualSorting: true
    // and read sorting state to send to your API.
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  // For compatibility with your TableHeader (v7 used prepareRow)
  const prepareRow = () => { };

  // If access is denied, return the AccessDenied component immediately
  if (error?.status === 403) {
    return <AccessDenied />;
  }

  return (
    <div className="datatable-wrapper">
      {/* ===== Header Section ===== */}
      <TableHeader

        tabledata={{
          Entries: showEntries,
          limit,
          onLimitChange,
          options: sizeOptions,
          search,
          SearchInputValue,
          handleSearch,
          copy,
          csv,
          pdf,
          print,
          modifiedColumns, // now v8 column defs
          rows,            // now v8 rows
          prepareRow,      // no-op, keeps your existing header tools stable
          fileName,
          user,
          isDate,
          footerRow: options?.footerRow,
        }}

        date={date}

      />


      <div className="table-responsive">
        {loading ? <TableGridSkeleton columns={columns} /> : <table className="table align-middle mb-0 table-sm small table-bordered">
          <thead className="fw-bold">
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted(); // 'asc' | 'desc' | false
                  return (
                    <th
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className="text-start user-select-none text-nowrap"
                      style={{
                        cursor: canSort ? "pointer" : "default",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {canSort && (
                        <span className="ms-1">
                          {sorted === "desc" ? (
                            <i className="bi bi-caret-down-fill"></i>
                          ) : sorted === "asc" ? (
                            <i className="bi bi-caret-up-fill"></i>
                          ) : (
                            ""
                          )}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td className="text-start text-nowrap" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-start text-muted py-3">
                  <span className="d-flex justify-content-center align-items-center py-sm-5 py-4">
                    <i className="bi bi-database-x fs-3 text-danger me-2 opacity-75"></i>
                    No data available
                  </span>
                </td>
              </tr>
            )}
          </tbody>

          {options?.footerRow && rows.length > 0 && (
            <tfoot className="table-light border-top border-2">
              <tr className="fw-bold text-dark">
                {headerGroups[0]?.headers.map((header) => {
                  const key = header.id || header.column.id;
                  console.log("header", header)
                  const val = options.footerRow[key] ?? "";
                  return (
                    <td
                      key={header.id}
                      className="text-start text-nowrap fw-bold py-2"
                    >
                      {val}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>}
        {error?.success === false && error?.message && <span className="text-danger px-3 pb-2 d-inline-block">Error: {error.message}</span>}
      </div>


      {isActive && (
        <Pagination
          page={meta.page}
          totalPages={meta.totalPages}
          hasPrev={meta.prevPage}
          hasNext={meta.nextPage}
          onPageChange={onPageChange}
          totalDocs={meta.totalDocs}
        />
      )}
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  options: PropTypes.object,
  Search: PropTypes.object,
  Entries: PropTypes.object,
  pagination: PropTypes.object,
};

export default DataTable;